import React, { useEffect, useRef, useState } from "react";
import { Send, ArrowLeft, MessageCircle, X, MailQuestion } from "lucide-react";
import { listConversations, listMessages, sendMessage } from "../api";

/**
 * Unified chat panel for both visitors and accounts.
 *
 * Props:
 * - role: "visitor" | "account" (purely for UI hint; backend determines from auth)
 * - onClose: optional callback for embedded modal usage
 * - autoOpenConvId: optional conversation id to open immediately
 */
export default function ChatPanel({ role = "visitor", onClose, autoOpenConvId = null }) {
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // conversation id
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  async function loadConvs() {
    setLoading(true);
    try {
      const r = await listConversations();
      setConversations(r.conversations || []);
    } catch (e) {
      setError("Sessione non valida. Accedi nuovamente.");
    } finally {
      setLoading(false);
    }
  }

  async function openConv(convId) {
    setActive(convId);
    try {
      const r = await listMessages(convId);
      setMessages(r.messages || []);
      // mark visited locally
      setConversations(arr => arr.map(c => c.id === convId
        ? { ...c, ...(role === "visitor" ? { visitor_unread: 0 } : { account_unread: 0 }) }
        : c));
    } catch {
      setMessages([]);
    }
  }

  useEffect(() => { loadConvs(); }, []);
  useEffect(() => { if (autoOpenConvId) openConv(autoOpenConvId); }, [autoOpenConvId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Poll every 8s while panel open
  useEffect(() => {
    if (!active) return;
    const id = setInterval(async () => {
      try {
        const r = await listMessages(active);
        setMessages(r.messages || []);
      } catch {}
    }, 8000);
    return () => clearInterval(id);
  }, [active]);

  async function send() {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const m = await sendMessage(active, { text: text.trim() });
      setMessages(arr => [...arr, m]);
      setText("");
      loadConvs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nell'invio");
    } finally { setSending(false); }
  }

  if (error) {
    return (
      <div className="p-8 text-center text-ink2" data-testid="chat-error">
        <MailQuestion size={32} className="mx-auto mb-3 text-terracotta"/>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-[80vh] md:max-h-[600px] bg-white" data-testid={`chat-panel-${role}`}>
      {/* Sidebar */}
      <aside className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-72 border-r border-borderline bg-parchment2/30`}>
        <div className="px-4 py-3 border-b border-borderline flex items-center justify-between">
          <h3 className="font-display text-lg text-ink flex items-center gap-2">
            <MessageCircle size={16} className="text-terracotta"/> Messaggi
          </h3>
          {onClose && (
            <button onClick={onClose} className="text-ink2 hover:text-terracotta md:hidden" data-testid="chat-close">
              <X size={16}/>
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="p-4 text-ink2 text-sm italic">Caricamento...</p>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-ink2 text-sm italic text-center">
              {role === "visitor"
                ? "Nessuna conversazione. Apri una chat dalle pagine attività, prodotti o eventi."
                : "Nessun messaggio ricevuto."}
            </p>
          ) : (
            <ul className="divide-y divide-borderline">
              {conversations.map(c => {
                const unread = role === "visitor" ? c.visitor_unread : c.account_unread;
                const isActive = active === c.id;
                const otherName = role === "visitor" ? c.account_name : c.visitor_name;
                const otherPic = role === "visitor" ? null : c.visitor_picture;
                return (
                  <li key={c.id}>
                    <button
                      data-testid={`conv-${c.id}`}
                      onClick={() => openConv(c.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-parchment ${isActive ? "bg-parchment border-l-2 border-terracotta" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        {otherPic ? (
                          <img src={otherPic} alt={otherName} className="w-8 h-8 rounded-full border border-borderline shrink-0"/>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-terracotta text-parchment text-xs font-mono flex items-center justify-center shrink-0">
                            {otherName?.[0]?.toUpperCase() || "?"}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-display text-sm text-ink truncate">{otherName}</span>
                            {unread > 0 && (
                              <span className="bg-terracotta text-parchment text-[9px] font-mono rounded-full px-1.5 py-0.5 shrink-0">{unread}</span>
                            )}
                          </div>
                          <p className="text-[11px] font-mono text-ink2 uppercase tracking-widest mt-0.5 truncate">
                            {c.target_kind} · {c.target_label}
                          </p>
                          {c.last_message && (
                            <p className="text-xs text-ink2 italic line-clamp-1 mt-1">
                              {c.last_sender === sender_role(role) ? "Tu: " : ""}{c.last_message}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* Conversation */}
      <main className={`${active ? "flex" : "hidden md:flex"} flex-col flex-1`}>
        {active ? (
          <>
            <div className="px-4 py-3 border-b border-borderline flex items-center gap-2">
              <button onClick={() => setActive(null)} className="md:hidden text-ink2 hover:text-terracotta">
                <ArrowLeft size={16}/>
              </button>
              <h3 className="font-display text-base text-ink truncate flex-1">
                {conversationLabel(conversations.find(c => c.id === active), role)}
              </h3>
              {onClose && (
                <button onClick={onClose} className="text-ink2 hover:text-terracotta hidden md:inline-flex" data-testid="chat-close-desktop">
                  <X size={16}/>
                </button>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-parchment2/20">
              {messages.length === 0 ? (
                <p className="text-center text-ink2 italic text-sm pt-8">Inizia la conversazione...</p>
              ) : messages.map(m => {
                const mine = m.sender_role === sender_role(role);
                return (
                  <div key={m.id} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    {!mine && m.sender_picture && (
                      <img src={m.sender_picture} alt="" className="w-7 h-7 rounded-full border border-borderline shrink-0"/>
                    )}
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-snug whitespace-pre-line ${
                      mine ? "bg-terracotta text-parchment rounded-br-none" : "bg-white border border-borderline rounded-bl-none"
                    }`}>
                      {!mine && <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name}</div>}
                      {m.text}
                      <div className={`text-[9px] font-mono mt-1 ${mine ? "text-parchment/60" : "text-ink2"} text-right`}>{timeAgo(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-borderline flex gap-2 bg-white">
              <input
                data-testid="chat-input"
                value={text} onChange={e => setText(e.target.value)}
                placeholder="Scrivi un messaggio..."
                maxLength={2000}
                className="flex-1 text-sm border border-borderline rounded-full px-4 py-2 bg-parchment focus:border-terracotta outline-none"
              />
              <button
                type="submit" disabled={sending || !text.trim()}
                data-testid="chat-send"
                className="bg-terracotta hover:bg-ink text-parchment rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50"
              ><Send size={14}/></button>
            </form>
          </>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center text-ink2 italic">
            Seleziona una conversazione
          </div>
        )}
      </main>
    </div>
  );
}

function sender_role(role) { return role; }

function conversationLabel(c, role) {
  if (!c) return "";
  return role === "visitor" ? c.account_name : `${c.visitor_name} · ${c.target_label || ""}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60); if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24); return days < 7 ? `${days}g` : d.toLocaleDateString("it-IT");
}
